import { Routes } from 'constants/index'
import { Button } from 'eezy-components'
import { getErrorMsg } from 'helpers/error'
import { observer } from 'mobx-react'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMatch, useNavigate } from 'react-router-dom'
import {
  GeneralReviewStore,
  PendingUsersReviewStore,
  ResourceStore,
} from 'stores'
import { Resource } from 'types/api'
import { useInstance } from 'util/di'
import AcceptModal from './AcceptModal'
import RejectModal from './RejectModal'

const PendingUsersReviewButton = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const pendingUsersMatch = useMatch('/admin/pending_users/:id')
  const contributorId = pendingUsersMatch?.params?.id

  const {
    reviewedResources,
    selectedVisible,
    submittingResources,
    setSubmittingResources,
    selectedRejectedReasons,
    selectedRejectedReasonLabels,
    submitReviewedResources,
    setReviewedResources,
    totalPendingCount,
    totalFilteredCount,
    setTotalPendingCount,
    setTotalFilteredCount,
    approveUserApplication,
    rejectUserApplication,
  } = useInstance<PendingUsersReviewStore>('PendingUsersReviewStore')
  const { visibleResources } = useInstance<ResourceStore>('ResourceStore')
  const { setFilesReviewedCount } =
    useInstance<GeneralReviewStore>('GeneralReviewStore')

  const [approvalModalOpen, setApprovalModalOpen] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)

  const selectedVisibleIds = selectedVisible.map(({ id }) => id)
  const submittableResources = reviewedResources.filter(reviewed =>
    selectedVisibleIds.includes(reviewed.resource.id)
  )
  const submittableCount = submittableResources.length

  const handleSubmit = async () => {
    if (selectedRejectedReasons.length > 0) {
      setRejectModalOpen(true)
    } else {
      setApprovalModalOpen(true)
    }
  }

  const submitResources = async (): Promise<Resource[]> => {
    const { succeededIds } = await submitReviewedResources(
      contributorId || '',
      reviewedResources
    )

    const successfulIds = succeededIds.map(String)

    setReviewedResources(
      reviewedResources.filter(
        ({ resource }) => !successfulIds.includes(String(resource.id))
      )
    )

    const remainingVisible = visibleResources
      .filter(res => !successfulIds.includes(String(res.id)))
      .map(res => res)

    const updatedCount = totalPendingCount - successfulIds.length
    const updatedFilteredCount = totalFilteredCount - successfulIds.length

    setTotalPendingCount(updatedCount)
    setTotalFilteredCount(updatedFilteredCount)
    setFilesReviewedCount(reviewedResources.length)

    return remainingVisible
  }

  const approveApplication = async () => {
    setSubmittingResources(true)
    try {
      await submitResources()
      await approveUserApplication(contributorId || '')
      enqueueSnackbar(
        t('review.pending_submissions.application_approval.success'),
        {
          variant: 'success',
        }
      )
      navigate(Routes.AdminPendingUsers)
    } catch (error) {
      enqueueSnackbar(t(getErrorMsg(error) || 'errors.requests.generic'), {
        variant: 'error',
      })
    } finally {
      setSubmittingResources(false)
    }
  }

  const rejectApplication = async () => {
    setSubmittingResources(true)
    try {
      await rejectUserApplication(contributorId || '')
      enqueueSnackbar(
        t('review.pending_submissions.application_rejection.success'),
        {
          variant: 'success',
        }
      )
      navigate(Routes.AdminPendingUsers)
    } catch (error) {
      enqueueSnackbar(t(getErrorMsg(error) || 'errors.requests.generic'), {
        variant: 'error',
      })
    } finally {
      setSubmittingResources(false)
    }
  }

  return (
    <>
      <Button
        color="primary"
        onClick={handleSubmit}
        loading={submittingResources}
        disabled={
          (!submittableCount || submittableCount < 25) &&
          selectedRejectedReasons.length === 0
        }
      >
        {t('util.submit')}
      </Button>

      {approvalModalOpen ? (
        <AcceptModal
          setOpen={setApprovalModalOpen}
          isOpen={approvalModalOpen}
          onConfirm={approveApplication}
          confirming={submittingResources}
        />
      ) : null}

      {rejectModalOpen ? (
        <RejectModal
          setOpen={setRejectModalOpen}
          isOpen={rejectModalOpen}
          onConfirm={rejectApplication}
          confirming={submittingResources}
          selectedRejectedReasons={selectedRejectedReasonLabels}
        />
      ) : null}
    </>
  )
}

export default observer(PendingUsersReviewButton)
